import React, { useEffect } from "react";
import { FiX } from "react-icons/fi";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const sizes = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
  full: "max-w-[1180px]",
};

export default function AdminDrawer({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  footer,
  size = "lg",
  zIndex = "z-[90]",
  closeOnBackdrop = true,
}) {
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 ${zIndex}`}
      aria-modal="true"
      role="dialog"
    >
      <button
        type="button"
        aria-label="Fermer"
        className="absolute inset-0 h-full w-full bg-slate-950/55 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      <aside
        className={cn(
          "absolute right-0 top-0 flex h-full w-[96vw] flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950",
          sizes[size] || sizes.lg,
        )}
      >
        <header className="border-b border-slate-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50 px-5 py-5 dark:border-slate-800 dark:from-slate-900 dark:via-slate-950 dark:to-sky-950/40 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              {Icon ? (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1277B9] to-cyan-400 text-xl text-white shadow-lg shadow-sky-500/25">
                  <Icon />
                </div>
              ) : null}

              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-slate-950 dark:text-white sm:text-xl">
                  {title}
                </h2>

                {subtitle ? (
                  <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    {subtitle}
                  </p>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FiX />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {children}
        </div>

        {footer ? (
          <footer className="border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950 sm:px-6">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>
  );
}